import {useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {ArrowUpRight, MessageSquareText, X} from 'lucide-react';
import {Link} from 'react-router-dom';
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  buttonVariants
} from '@kroll-resolver-crisp/ui-components-react';
import {type SubmissionCommentFeedback} from '@/api/submissionComments';
import {useSubmissionCommentFeedback, useAcknowledgeSubmissionComment} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';

// Same cap and "+N more" behaviour as QaFeedbackWidget beside it - dashboard real estate is
// precious and neither widget may grow unboundedly.
const MAX_VISIBLE_CARDS = 3;

// Reviewer comments written about this analyst's own submissions, as dismissible alerts.
//
// A sibling of QaFeedbackWidget rather than rows inside it: that widget is specifically "rejected
// submissions" and every entry in it is a reviewer's verdict against the analyst ("you said X, QA says Y", or a QC rejection).
// A coaching note has no verdict, so folding the two together would mean a vaguer heading over two
// different kinds of thing. Same visual language, same dismiss gesture, separate meaning.
export function ReviewerCommentsWidget() {
  const {data, isLoading} = useSubmissionCommentFeedback();
  const acknowledge = useAcknowledgeSubmissionComment();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAll, setShowAll] = useState(false);

  const items = data ?? [];
  if (isLoading && items.length === 0) {
    return null;
  }

  const visible = items.filter((item) => !dismissedIds.has(item.comment.id));
  if (visible.length === 0) {
    return null;
  }

  const shown = visible.slice(0, MAX_VISIBLE_CARDS);
  const remaining = visible.length - shown.length;

  function handleDismiss(id: string) {
    setDismissedIds((previous) => new Set(previous).add(id));
    setErrors((previous) =>
      Object.fromEntries(Object.entries(previous).filter(([key]) => key !== id))
    );

    acknowledge.mutate(id, {
      onError(error) {
        // Un-hide rather than leaving the UI lying about what actually got dismissed.
        setDismissedIds((previous) => {
          const next = new Set(previous);
          next.delete(id);
          return next;
        });
        setErrors((previous) => ({
          ...previous,
          [id]: getApiErrorMessage(error, 'Failed to dismiss comment.')
        }));
      }
    });
  }

  return (
    <section>
      <h3 className="font-heading mb-3 text-sm font-medium">Reviewer comments</h3>

      <div className="space-y-2">
        {shown.map((item) => (
          <ReviewerCommentAlert
            key={item.comment.id}
            item={item}
            error={errors[item.comment.id]}
            onDismiss={() => {
              handleDismiss(item.comment.id);
            }}
          />
        ))}
      </div>

      {remaining > 0 ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-1 px-0"
          onClick={() => {
            setShowAll(true);
          }}>
          +{remaining} more {remaining === 1 ? 'comment' : 'comments'} from reviewers
        </Button>
      ) : null}

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Reviewer comments</DialogTitle>
            <DialogDescription>
              All {visible.length} {visible.length === 1 ? 'comment' : 'comments'} left on your
              submissions.
            </DialogDescription>
          </DialogHeader>

          {/* The body is the one part that scrolls, so the title stays put while a long list is
              read; the alerts' own buttons give it a tab stop. */}
          <DialogBody>
            {visible.map((item) => (
              <ReviewerCommentAlert
                key={item.comment.id}
                item={item}
                error={errors[item.comment.id]}
                onDismiss={() => {
                  handleDismiss(item.comment.id);
                }}
              />
            ))}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Through to the submission itself rather than out to the content: the alert carries only enough to
// recognise which submission this is, and everything the analyst needs in order to act on the
// comment - classification, enrichment, platform status, the rest of the thread - lives on the row.
// `q` is what narrows the list server-side (it matches the dedup key, which embeds the external id);
// `submission` only decides which of the matching rows opens.
function submissionHref(item: SubmissionCommentFeedback): string {
  const parameters = new URLSearchParams({submission: item.comment.submission_id});
  if (item.item_external_id) {
    parameters.set('q', item.item_external_id);
  }

  return `/submissions?${parameters.toString()}`;
}

function ReviewerCommentAlert({
  item,
  error,
  onDismiss
}: {
  readonly item: SubmissionCommentFeedback;
  readonly error: string | undefined;
  readonly onDismiss: () => void;
}) {
  const {comment} = item;
  const title = item.display_name ?? item.item_external_id ?? comment.submission_id;
  const href = submissionHref(item);

  return (
    <Alert>
      <MessageSquareText />
      <AlertTitle className="flex flex-wrap items-center gap-1.5">
        <Link to={href} className="line-clamp-1 min-w-0 hover:underline">
          {title}
        </Link>
        <Badge variant="outline">{item.policy_violation}</Badge>
      </AlertTitle>
      <AlertDescription className="grid gap-1.5">
        <p className="text-foreground whitespace-pre-wrap">{comment.body}</p>
        <p>
          From{' '}
          <span className="text-foreground font-medium">
            {comment.author_email.split('@')[0] ?? comment.author_email}
          </span>{' '}
          · {formatDistanceToNow(new Date(comment.created_at), {addSuffix: true})}
        </p>
        {error ? <p className="text-destructive-ink font-medium">{error}</p> : null}
      </AlertDescription>
      <AlertAction className="flex items-center gap-1">
        <Link
          to={href}
          aria-label="Open submission"
          className={buttonVariants({variant: 'outline', size: 'icon-xs'})}>
          <ArrowUpRight />
        </Link>
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          aria-label="Dismiss comment"
          onClick={onDismiss}>
          <X />
        </Button>
      </AlertAction>
    </Alert>
  );
}
